import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'
import config from '@/config'
import { File, Signature, Signer } from '@/modules/signature/types'

const { dateFormat: { api: apiDateFormat } } = config

export const SIGNER_COLOR_CODES: string[] = [
  '#6DDDF3',
  '#ADDC66',
  '#FBDD67',
  '#FF900E',
  '#CA3000',
  '#F696C4',
  '#C975FC',
  '#3264E1',
  '#4C6E17',
  '#AA8E21',
  '#794202',
  '#1F2E55',
  '#6DDDF3',
  '#7426A4',
  '#767676',
]
export const emptySigner: Signer = {
  id: null,
  email: null,
  name: null,
  signatures: [],
  multiSelectType: null,
  color: SIGNER_COLOR_CODES[0],
}

export enum SignatureWizardSteps {
  documentSelection = 'documentSelection',
  form = 'form',
  signaturePlacement = 'signaturePlacement',
  signing = 'signing',
}
export interface SignatureEditStateInterface {
  activeStep: SignatureWizardSteps,
  activeSigner: Signer,
  signers: Signer[],
  message: string,
  dueDate: string,
  file?: File,
  isFinished: boolean
}

const initialState: SignatureEditStateInterface = {
  activeStep: SignatureWizardSteps.documentSelection,
  activeSigner: null,
  file: undefined,
  signers: [emptySigner],
  message: null,
  dueDate: moment().add(1, 'day').format(apiDateFormat),
  isFinished: false,
}

const signatureEditSlice = createSlice({
  name: 'edit',
  initialState,
  reducers: {
    setActiveStep(state, action: PayloadAction<{ step: SignatureWizardSteps }>) {
      state.activeStep = action.payload.step
    },
    setActiveSigner(state, action: PayloadAction<{ signer }>) {
      state.activeSigner = action.payload.signer
    },
    setFile(state, action: PayloadAction<{ file: File }>) {
      state.file = action.payload.file
    },
    removeFile(state) {
      state.file = undefined
    },
    setSigners(state, action: PayloadAction<{ signers: Signer[] }>) {
      state.signers = action.payload.signers
    },
    addSigner(state, action: PayloadAction<{ signer: Signer }>) {
      const { signer } = action.payload
      const colorCode = SIGNER_COLOR_CODES[state.signers?.length ? state.signers.length - 1 : 0]
      state.signers = [...state.signers, { ...signer, color: colorCode }]
    },
    removeSigner(state, action: PayloadAction<{ index: number }>) {
      const { index } = action.payload
      state.signers.splice(index, 1)
    },
    changeSigner(state, action: PayloadAction<{ index: number, signer: Signer }>) {
      const { signers } = state
      const { index, signer } = action.payload

      if (!signer) {
        signers[index] = initialState.signers[0] // empty signer
      } else {
        signers[index] = { ...signer, color: signer.color ?? SIGNER_COLOR_CODES[index] }
      }
    },
    changeMessage(state, action: PayloadAction<{ message: string }>) {
      state.message = action.payload.message
    },
    changeDueDate(state, action: PayloadAction<{ dueDate: string }>) {
      state.dueDate = action.payload.dueDate
    },
    addSignaturesToSigner(state, action: PayloadAction<{ signerEmail: string, signatures: Signature[] }>) {
      const { signers } = state
      const { signerEmail, signatures } = action.payload

      const signerIndex = signers
        .findIndex((signer) => (signerEmail === signer.email))
      if (signerIndex < 0) { return { ...state } }

      signers[signerIndex].signatures = signatures
    },
    setIsFinished(state, action: PayloadAction<boolean>) {
      state.isFinished = action.payload
    },
    reset() {
      return { ...initialState }
    },
  },
})

export const {
  setActiveStep,
  setActiveSigner,
  setFile,
  removeFile,
  setSigners,
  addSigner,
  removeSigner,
  changeSigner,
  changeMessage,
  changeDueDate,
  addSignaturesToSigner,
  setIsFinished,
  reset,
} = signatureEditSlice.actions
export default signatureEditSlice.reducer
